import React from 'react';
import { Text, TouchableOpacity, View, StyleSheet, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { FontAwesome } from '@expo/vector-icons';
import I18n from 'i18n-js';
import useWidth from '../scanToCartScanning/useWidth';
import { useDispatch, useSelector } from 'react-redux';
import { findToteForNextItem, handleItemOutOfStock, moveToNextItem, requestReplenish, setNextPickItem, setUnScannedItems } from '../../actions/scanToCartActions';
import { sharedPromptStyles, basePromptContainer, baseResponseView, baseTitleText, baseButtonText, baseActionButton } from './shared/promptStyles';

const OutOfStockPrompt = ({ showPrompt, setShowPrompt }) => {
  const windowWidth = useWidth();
  const { nextPickItem, activeCart, unScannedItems, toteAssignments } = useSelector(state => state.scanToCart);
  const isDesktop = (windowWidth || 0) >= 900;
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [activeTooltip, setActiveTooltip] = React.useState(null);

  const handleRequestReplenish = async () => {
    setLoading(true);
    const data = {
      sku: nextPickItem?.item?.sku,
      location: nextPickItem?.item.location ?? ' '
    }

    await requestReplenish(data, () => {
      setLoading(false)})

    if (unScannedItems && unScannedItems.length > 1) {
      const shuffledItems = moveToNextItem(unScannedItems);
      const nextPickItemData = findToteForNextItem(shuffledItems,toteAssignments)
      dispatch(setUnScannedItems(shuffledItems));
      dispatch(setNextPickItem(nextPickItemData));
    }
    setShowPrompt(false);
  };

  const handleHoldOrders = () => {
    setLoading(true);
    const data = {
      order_item_id: nextPickItem?.item.order_item_id,
      product_id: nextPickItem?.item.product_id,
      option: 'hold_item',
      cart_id: activeCart?.cart_id
    };

    dispatch(handleItemOutOfStock(data, () => {
      setLoading(false);
      setShowPrompt(false);
    }));
  };

  const handleClosePrompt = () => {
    setShowPrompt(false);
  };

  const GradientButton = ({ text, onPress, testID }) => (
    <TouchableOpacity testID={testID} onPress={onPress} style={styles.actionButtonTouchable}>
      <LinearGradient
        locations={[0, 0.7, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        colors={['#ffb636', '#ffb636', '#d47d02']}
        style={styles.actionButton}>
        <Text style={styles.buttonText}>{text}</Text>
      </LinearGradient>
    </TouchableOpacity>
  );

  const PromptContainer = ({ title, buttons }) => {
    const mainButtons = buttons.filter(button => button.testID !== "Cancel");
    const cancelButton = buttons.find(button => button.testID === "Cancel");
    
    return (
      <LinearGradient
        locations={[0, 1]}
        start={{ x: 0, y: 1 }}
        end={{ x: 0, y: 0 }}
        colors={['#d47d02', '#ffb636']}
        style={[isDesktop ? styles.promptContainer : styles.promptContainerMobile]}>

        <View style={styles.cancelIcon}>
          <TouchableOpacity testID="CancelIcon" onPress={handleClosePrompt}>
            <i className="icon-cancel" style={{ fontSize: 26, color: '#1C1C1C' }}></i>
          </TouchableOpacity>
        </View>

        {!loading && (
          <View style={styles.titleContainer}>
            <Text
              style={[isDesktop ? styles.titleText : styles.titleTextMobile]}>
              {title}
            </Text>
          </View>
        )}

        <View style={[isDesktop ? styles.buttonContainer : styles.buttonContainerMobile]}>
          {loading ? (
            <ActivityIndicator size="large" color="#1C1C1C" />
          ) : (
            mainButtons.map((button, index) => (
              <View key={index} style={styles.buttonWithTooltip}>
                <View style={styles.buttonRow}>
                  <GradientButton
                    testID={button.testID}
                    text={button.text}
                    onPress={button.onPress}
                  />
                  {button.tooltip && (
                    <TouchableOpacity
                      testID={`${button.testID}Info`}
                      style={styles.infoIconWrapper}
                      onPress={() => setActiveTooltip(activeTooltip === button.testID ? null : button.testID)}>
                      <FontAwesome name="info-circle" size={22} color={'#1C1C1C'} />
                    </TouchableOpacity>
                  )}
                </View>
                {activeTooltip === button.testID && button.tooltip && (
                  <View style={styles.tooltipContainer}>
                    <Text style={styles.tooltipText}>{button.tooltip}</Text>
                  </View>
                )}
              </View>
            ))
          )}

          {cancelButton && !loading && (
            <TouchableOpacity 
              testID={cancelButton.testID} 
              onPress={cancelButton.onPress} 
              style={styles.cancelButtonWrapper}
            >
              <LinearGradient
                locations={[0, 0.7, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                colors={['#ffb636', '#ffb636', '#d47d02']}
                style={[
                  isDesktop ? styles.cancelButton : styles.cancelButtonMobile
                ]}>
                <Text
                  style={[
                    isDesktop
                      ? styles.cancelButtonText
                      : styles.cancelButtonTextMobile
                  ]}>
                  {cancelButton.text}
                </Text>
              </LinearGradient>
            </TouchableOpacity>
          )}
        </View>
      </LinearGradient>
    );
  };

  const promptButtons = [
    {
      testID: "RequestReplenish",
      text: I18n.t('stockIssue.replenishButton'),
      onPress: handleRequestReplenish,
      tooltip: I18n.t('stockIssue.replenishTooltip')
    },
    {
      testID: "HoldOrders",
      text: I18n.t('stockIssue.holdOrdersButton'),
      onPress: handleHoldOrders,
      tooltip: I18n.t('stockIssue.holdOrdersTooltip')
    },
    {
      testID: "Cancel",
      text: I18n.t('stockIssue.cancelButton'),
      onPress: handleClosePrompt
    }
  ];

  const promptTitle = I18n.t('stockIssue.title');

  return (
    <View style={[styles.responseView, { display: !showPrompt && 'none' }]}>
      <PromptContainer
        title={promptTitle}
        buttons={promptButtons}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    ...baseResponseView,
    justifyContent: 'center',
    top: 20,
    zIndex: 99999,
  },
  promptContainer: {
    ...basePromptContainer,
    maxWidth: 550,
  },
  cancelIcon: sharedPromptStyles.cancelIcon,
  titleContainer: sharedPromptStyles.titleContainer,
  titleText: baseTitleText,
  buttonContainer: sharedPromptStyles.buttonContainer,
  buttonWithTooltip: {
    width: '100%',
    marginBottom: 5
  },
  buttonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  actionButtonTouchable: {
    flex: 1
  },
  actionButton: {
    ...baseActionButton,
    paddingVertical: 12,
    width: '100%',
  },
  infoIconWrapper: {
    marginLeft: 10,
    padding: 5
  },
  tooltipContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: 12,
    borderRadius: 5,
    marginTop: 5,
    width: '100%'
  },
  tooltipText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    lineHeight: 20
  },
  buttonText: {
    ...baseButtonText,
    textAlign: 'center',
    flex: 1
  },
  cancelButtonWrapper: {
    alignSelf: 'center',
    marginTop: 10
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: '#1C1C1C',
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center'
  },
  cancelButtonText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
    fontWeight: '500',
    textAlign: 'center'
  },
  // Mobile styles
  promptContainerMobile: {
    ...basePromptContainer,
    paddingHorizontal: 15,
    paddingVertical: 12,
    maxWidth: 400,
    marginTop: '30%',
  },
  titleTextMobile: {
    ...baseTitleText,
    fontSize: 18
  },
  buttonContainerMobile: sharedPromptStyles.buttonContainerMobile,
  cancelButtonMobile: {
    borderWidth: 1,
    borderColor: '#1C1C1C',
    borderRadius: 20,
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  cancelButtonTextMobile: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
    fontWeight: '500',
    textAlign: 'center'
  }
});

export default OutOfStockPrompt;
